import React, { useEffect, useState } from 'react';
import { Content, InfoCard, Header } from '@backstage/core-components';
import {
  Grid,
  FormControl,
  TextField,
  Button,
  Typography,
  Snackbar,
  CircularProgress,
  InputLabel,
  MenuItem,
  makeStyles,
  Box,
  Divider,
  alpha,
} from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import MuiAlert from '@material-ui/lab/Alert';
import {
  useApi,
  identityApiRef,
  configApiRef,
  alertApiRef,
} from '@backstage/core-plugin-api';
import Select from '@material-ui/core/Select';
import { useEpDevPNotifications } from '@solace-labs/backstage-plugin-ep-devp-react';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@material-ui/pickers';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';

const useStyles = makeStyles((theme) => ({
  datePicker: {
    '& .MuiOutlinedInput-root': {
      // Add background color for dark mode
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
    },
    '& .MuiInputBase-input': {
      // Ensure text is visible in dark mode
      color: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      // Make border more visible in dark mode
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'inherit',
    },
    // Fix the calendar icon color in dark mode
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
    }
  },
  content: {
    flex: "1 1 auto",
    height: "95vh",
    overflowY: "auto",
    '&::-webkit-scrollbar': {
      height: "18px",
      width: "18px",
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundClip: "padding-box",
      backgroundColor: alpha(theme.palette.text.primary, 0.15),
      border: "4px solid rgba(0, 0, 0, 0)",
      borderRadius: "9999px",
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.35),
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: "transparent",
    },
  },
  navigation: {
    backgroundColor: theme.palette.background.paper,
    height: '4rem',
    width: '100%',
    position: 'sticky',
    bottom: 0,
    zIndex: 100,
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

// Custom form field component similar to EpDevPFormTextField
const EpDevPFormTextField = ({ name, control, label, rules, size, ...props }) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          {...props}
          label={label}
          size={size}
          fullWidth
          variant="outlined"
          error={!!error}
          helperText={error ? error.message : ''}
        />
      )}
    />
  );
};

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

type UserRoleResponse = {
  userID: string;
  roleID: string;
  roleName: string;
};

interface ContractFormData {
  startDate: string;
  endDate: string;
  contractName: string;
  contractNumber: string;
  contractStatus: string;
  subscription?: string;
  application: string;
  document?: File;
}

export const ContractForm = ({ userRole: initialUserRole }) => {
  const { t } = useServiceRegistryTranslation();
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();
  // const [userRole, setUserRole] = useState<UserRoleResponse | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const alertApi = useApi(alertApiRef);
  const [minEndDate, setMinEndDate] = useState(
    new Date().toISOString().split('T')[0],
  );
  const classes = useStyles();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ContractFormData>({
    defaultValues: {
      startDate: '',
      endDate: '',
      contractName: '',
      contractNumber: '',
      contractStatus: '',
      application: '',
      subscription: '',
    },
  });

  // Watch the startDate field to update minEndDate
  const startDate = watch('startDate');
  const createNotification = useEpDevPNotifications();

  useEffect(() => {
    if (startDate) {
      const start = new Date(startDate);
      const threeMonthsLater = new Date(start.setDate(start.getDate() + 91))
        .toISOString()
        .split('T')[0];
      setMinEndDate(threeMonthsLater);
    }
  }, [startDate]);

  // Fetch user role function
  // const fetchUserRole = async () => {
  //   try {
  //     setLoading(true);
  //     const profile = await identityApi.getProfileInfo();
  //     const { token } = await identityApi.getCredentials();
  //     const response = await fetch(`${baseUrl}/api/users/Role`, {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: JSON.stringify({ email: profile.email }),
  //     });

  //     if (!response.ok) {
  //       throw new Error(`Failed to fetch user role: ${response.statusText}`);
  //     }

  //     const userRoleResponse: UserRoleResponse = await response.json();
  //     setUserRole(userRoleResponse);
  //   } catch (error) {
  //     console.error(error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchApplications = async () => {
    try {
      setLoadingApplications(true);
      const { token } = await identityApi.getCredentials();

      const response = await fetch(
        `${baseUrl}/api/ep-devp-manage-apps/nav-canada`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch applications: ${response.statusText}`);
      }

      const result = await response.json();
      setApplications(result.list || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingApplications(false);
    }
  };

  const fetchSubscriptions = async (applicationId: string) => {
    try {
      const { token } = await identityApi.getCredentials();
      setLoadingSubscriptions(true);
      const response = await fetch(
        `${baseUrl}/api/contract/subscriptions/${applicationId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch subscriptions: ${response.statusText}`,
        );
      }

      const result = await response.json();
      setSubscriptions(result.subscriptions);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    // fetchUserRole();
    reset(prevValues => ({
      ...prevValues,
      contractStatus: '',
      application: '',
      subscription: '',
    }));
  }, []);

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const validateDateYear = (dateString: string) => {
    const date = new Date(dateString);
    if (date.getFullYear() >= 2100) {
      return t('contracts.invalidYear');
    }
    return true;
  };

  const onSubmit = async (data: ContractFormData) => {
    setLoading(true);

    if (!initialUserRole) {
      // await fetchUserRole();
    }
    if (initialUserRole) {
      try {
        const applicationName =
          applications.find(app => app.entityId.id === data.application)
            ?.entityId.displayName || '';
        const subscriptionName =
          subscriptions.find(sub => sub.id === data.subscription)?.name || '';

        const formData = new FormData();
        formData.append('startDate', data.startDate);
        formData.append('endDate', data.endDate);
        formData.append('contractName', data.contractName);
        formData.append('contractNumber', data.contractNumber);
        formData.append('contractStatus', data.contractStatus);
        formData.append('application', data.application);
        formData.append('subscription', data.subscription || '');
        formData.append('applicationname', applicationName);
        formData.append('subscriptionname', subscriptionName);
        formData.append('userId', initialUserRole.userID);
        if (data.document && data.document instanceof File) {
          formData.append('document', data.document);
        }
        const { token } = await identityApi.getCredentials();
        const response = await fetch(`${baseUrl}/api/contract/add`, {
          method: 'POST',
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        });

        const result = await response.json();

        if (response.ok) {
          createNotification(t('contracts.added'));

          navigate(`/contract/view`);
          // Reset form and generate a new contract number
          reset({
            startDate: '',
            endDate: '',
            contractName: '',
            contractNumber: '',
            contractStatus: '',
            application: '',
            subscription: '',
            document: undefined,
          });
        } else {
          const errorMessage =
            result.error ===
              'Contract dates are overlapping with an existing contract'
              ? t('contracts.overlapping')
              : result.error ===
                'Contract already exists with same contract number'
                ? t('contracts.duplicateNumber')
                : result.error == "Contract already exists with same contract name" ?
                  t('contracts.duplicateName')
                  : t('contracts.addFailed');
          alertApi.post({ message: errorMessage, severity: 'error', display: 'transient' });
        }
      } catch (error) {
        alertApi.post({ message: t('contracts.addFailed'), severity: 'error', display: 'transient' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/contract/view');
  };

  return (
    <>
      <Header title={t('contracts.title')} subtitle={t('contracts.add')} />
      <Box flexGrow={1} padding={3} className={classes.content}>
        <Content>
          <div className="contract-form-container">
            <InfoCard className="contract-card">
              <form id="contract-form" className="contract-form" noValidate>
                <Grid container spacing={3}>
                  {/* Contract Name */}
                  <Grid item xs={12}>
                    <EpDevPFormTextField
                      name="contractName"
                      control={control}
                      label={`${t('contracts.name')} *`}
                      rules={{
                        required: t('contracts.required', { field: t('contracts.name') }),
                        minLength: {
                          value: 2,
                          message: t('contracts.nameMin'),
                        },
                        maxLength: {
                          value: 15,
                          message:
                            t('contracts.nameMax'),
                        },
                      }}
                      inputProps={{
                        maxLength: 15,
                      }}
                      size="small"
                    />
                  </Grid>

                  {/* Contract Number and Status in one row */}
                  <Grid item xs={6}>
                    <EpDevPFormTextField
                      name="contractNumber"
                      control={control}
                      label={`${t('contracts.number')} *`}
                      rules={{
                        required: t('contracts.required', { field: t('contracts.number') }),
                        minLength: {
                          value: 6,
                          message: t('contracts.numberMin'),
                        },
                        maxLength: {
                          value: 10,
                          message:
                            t('contracts.numberMax'),
                        },
                        pattern: {
                          value: /^[0-9]+$/,
                          message: t('contracts.numberDigits'),
                        },
                      }}
                      size="small"
                      inputProps={{
                        maxLength: 10,
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name="contractStatus"
                      control={control}
                      rules={{ required: t('contracts.required', { field: t('contracts.status') }) }}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth size='small'>
                          <InputLabel error={!!errors.contractStatus}>{t('contracts.status')} *</InputLabel>
                          <Select
                            {...field}
                            label={t('contracts.status')}
                            onChange={event =>
                              field.onChange((event.target as HTMLInputElement).value)
                            }
                            error={!!errors.contractStatus}
                          >
                            <MenuItem value="Not Started">{t('contracts.notStarted')}</MenuItem>
                            <MenuItem value="In Progress">{t('contracts.inProgress')}</MenuItem>
                            <MenuItem value="Signed">{t('contracts.signed')}</MenuItem>
                            <MenuItem value="Expired">{t('contracts.expired')}</MenuItem>
                            <MenuItem value="Cancelled">{t('contracts.cancelled')}</MenuItem>
                            <MenuItem value="Deleted">{t('contracts.deleted')}</MenuItem>
                          </Select>
                          {errors.contractStatus && (
                            <Typography
                              color="error"
                              style={{ fontSize: 'smaller', marginLeft: '1rem', color: '#f44336' }}
                            >
                              {errors.contractStatus.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {/* Start Date and End Date in one row */}
                  {/* <Grid item xs={6}>
                    <Controller
                      name="startDate"
                      control={control}
                      rules={{ required: t('contracts.required', { field: t('contracts.startDate') }), validate: validateDateYear }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="date"
                          className={classes.datePicker}
                          fullWidth
                          variant="outlined"
                          label="Start Date *"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.startDate}
                          helperText={
                            errors.startDate ? errors.startDate.message : ''
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name="endDate"
                      control={control}
                      rules={{
                        required: 'End date is required',
                        validate: value => {
                          const start = new Date(startDate);
                          const end = new Date(value);
                          const yearValidation = validateDateYear(value);

                          // Create a date 3 months later than start date
                          const threeMonthsLater = new Date(start);
                          threeMonthsLater.setDate(start.getDate() + 91);

                          if (end < threeMonthsLater) {
                            // Format the threeMonthsLater date to YYYY-MM-DD format
                            const formattedMinDate = threeMonthsLater.toISOString().split('T')[0];
                            return `The end date should be ${formattedMinDate} or later`;
                          }

                          if (yearValidation !== true) {
                            return 'Invalid Year';
                          }

                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="date"
                          fullWidth
                          className={classes.datePicker}
                          variant="outlined"
                          label="End Date *"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.endDate}
                          helperText={errors.endDate ? errors.endDate.message : ''}
                        />
                      )}
                    />
                  </Grid> */}

                  <Grid item xs={6}>
                    <Controller
                      name="startDate"
                      control={control}
                      rules={{ required: t('contracts.required', { field: t('contracts.startDate') }), validate: validateDateYear }}
                      render={({ field: { onChange, value } }) => (
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                          <DatePicker
                            label={`${t('contracts.startDate')} *`}
                            value={value ? new Date(value) : null}
                            onChange={(date) => {
                              // Convert date to YYYY-MM-DD format to match your existing logic
                              onChange(date ? date.toISOString().split('T')[0] : '');
                            }}
                            format="MM/dd/yyyy"
                            fullWidth
                            size='small'
                            inputVariant="outlined"
                            variant="inline"
                            className={classes.datePicker}
                            error={!!errors.startDate}
                            helperText={errors.startDate ? errors.startDate.message : ''}
                          // Optional: Add min/max date constraints if needed
                          // minDate={new Date()}
                          />
                        </MuiPickersUtilsProvider>
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name="endDate"
                      control={control}
                      rules={{
                        required: t('contracts.required', { field: t('contracts.endDate') }),
                        validate: value => {
                          const start = new Date(startDate);
                          const end = new Date(value);
                          const yearValidation = validateDateYear(value);

                          // Create a date 3 months later than start date
                          // Create a date 6 months later than end date
                          const threeMonthsLater = new Date(start);
                          threeMonthsLater.setDate(start.getDate() + 91);

                          if (end < threeMonthsLater) {
                            // Format the threeMonthsLater date to YYYY-MM-DD format
                            const formattedMinDate = threeMonthsLater.toISOString().split('T')[0];
                            return t('contracts.endDateMinimum', { date: formattedMinDate });
                          }

                          if (yearValidation !== true) {
                            return t('contracts.invalidYear');
                          }

                          return true;
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                          <DatePicker
                            label={`${t('contracts.endDate')} *`}
                            value={value ? new Date(value) : null}
                            onChange={(date) => {
                              // Convert date to YYYY-MM-DD format to match your existing logic
                              onChange(date ? date.toISOString().split('T')[0] : '');
                            }}
                            format="MM/dd/yyyy"
                            fullWidth
                            inputVariant="outlined"
                            variant="inline"
                            size='small'
                            className={classes.datePicker}
                            error={!!errors.endDate}
                            helperText={errors.endDate ? errors.endDate.message : ''}
                          // Optional: Set minimum date based on start date + 3 months
                          // minDate={startDate ? (() => {
                          //   const start = new Date(startDate);
                          //   start.setDate(start.getDate() + 91);
                          //   return start;
                          // })() : new Date()}
                          />
                        </MuiPickersUtilsProvider>
                      )}
                    />
                  </Grid>

                  {/* Application and Subscription in one row */}
                  <Grid item xs={6}>
                    <Controller
                      name="application"
                      control={control}
                      rules={{ required: t('contracts.selectApplication') }}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth size='small'>
                          <InputLabel error={!!errors.application}>{t('contracts.application')} *</InputLabel>
                          <Select
                            {...field}
                            label={t('contracts.application')}
                            onChange={event => {
                              const applicationId = (event.target as HTMLInputElement)
                                .value;
                              field.onChange(applicationId);
                              fetchSubscriptions(applicationId);
                            }}
                            error={!!errors.application}
                          >
                            {loadingApplications ? (
                              <MenuItem disabled>{t('contracts.loading')}</MenuItem>
                            ) : (
                              applications.map((app, index) => (
                                <MenuItem key={index} value={app.entityId.id}>
                                  {app.entityId.displayName}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                          {errors.application && (
                            <Typography
                              color="error"
                              style={{ fontSize: 'smaller', marginLeft: '1rem', color: '#f44336' }}
                            >
                              {errors.application.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name="subscription"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth size='small'>
                          <InputLabel>{t('contracts.subscription')}</InputLabel>
                          <Select
                            {...field}
                            label={t('contracts.subscription')}
                            onChange={event =>
                              field.onChange((event.target as HTMLInputElement).value)
                            }
                            error={!!errors.subscription}
                          >
                            {loadingSubscriptions ? (
                              <MenuItem disabled>{t('contracts.loading')}</MenuItem>
                            ) : subscriptions.length > 0 ? (
                              subscriptions.map((subscription, index) => (
                                <MenuItem key={index} value={subscription.id}>
                                  {subscription.name}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>{t('contracts.noSubscription')}</MenuItem>
                            )}
                          </Select>
                          {errors.subscription && (
                            <Typography
                              color="error"
                              style={{ fontSize: 'smaller', marginLeft: '1rem', color: '#f44336' }}
                            >
                              {errors.subscription.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="document"
                      control={control}
                      render={({ field: { onChange, value, ...field } }) => (
                        <TextField
                          {...field}
                          type="file"
                          fullWidth
                          variant="outlined"
                          size='small'
                          label={t('contracts.uploadDocument')}
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.document}
                          helperText={errors.document?.message || t('contracts.fileHelp')}
                          inputProps={{
                            accept: ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          }}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (e.target.files && e.target.files.length > 0) {
                              const file = e.target.files[0];
                              const validTypes = [
                                'application/pdf',
                                'application/msword',
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                              ];
                              const maxSize = 10 * 1024 * 1024; // 10 MB in bytes

                              if (!validTypes.includes(file.type)) {
                                e.target.value = '';
                                setError('document', {
                                  type: 'manual',
                                  message: t('contracts.fileTypeError'),
                                });
                                onChange(undefined);
                              } else if (file.size > maxSize) {
                                e.target.value = '';
                                setError('document', {
                                  type: 'manual',
                                  message: t('contracts.fileSizeError'),
                                });
                                onChange(undefined);
                              } else {
                                clearErrors('document');
                                onChange(file);
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </form>
            </InfoCard>
          </div>
        </Content>
      </Box>

      <Divider />

      {/* Action buttons at the bottom, matching CreateConsumerAppForm */}
      <Box display="flex" alignItems="center" justifyContent="space-between" className={classes.navigation}>
        <Box ml={2}>
          <Button
            color="secondary"
            variant="text"
            data-testid="cancel-button"
            onClick={() => navigate('/contract/view')}
          >
            {t('contracts.cancel')}
          </Button>
        </Box>
        <Box position="relative" mr={3}>
          <Button
            color="primary"
            variant="contained"
            disabled={loading}
            data-testid="submit-button"
            onClick={handleSubmit(onSubmit)}
          >
            {t('contracts.add')}
          </Button>
          {loading && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </Box>
      </Box>
    </>
  );
};
